import React, { useState, useEffect, useRef } from "react";
import {
  CapabilityNavigationBase,
  ScrollContainer,
  Section,
  ScrollArrow,
  ArrowContainer,
  PageNav,
  JumpText,
  CapabilityNavigationContent,
} from "./page-navigation.styled";
import { replaceWithDashes } from "../../utils/replaceWithDashes";
import { NavigationTitleSliceFragment } from "../../types/graphql";
import * as FA from "../../styles/fontawesome";

interface IPageNavigationProps {
  refs: React.RefObject<HTMLDivElement>[];
  pageNavigation: NavigationTitleSliceFragment[];
}

const PageNavigation = (props: IPageNavigationProps) => {
  const { refs } = props;

  const [activeSection, setActiveSection] = useState(0);
  // Ref and variable to determine offset from left of the current section title is
  const activeSectionRef: any = useRef(null);
  const [sectionToTopDistance, setSectionToTopDistance] = useState(0);

  // Ref and variable to determine the width of all the section titles
  const nav: any = useRef(null);

  // Current scroll position. Passed to navigation component
  const [scrollPosition, setScrollPosition] = useState(0);

  // Mobile nav
  const [isNavOpen, setIsNavOpen] = useState(false);

  let currentHash: string;

  const scrollPageNav = () => {
    // Set current section position from left as state
    setSectionToTopDistance(activeSectionRef.current?.offsetTop);
    nav.current.scrollTo({
      top: sectionToTopDistance,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    // Get hash for scrolling to page section
    currentHash = location.hash.replace("#", "");

    // Get ref index that match hash
    const hashMatchIndex = refs.findIndex(
      (ref) => ref.current?.id?.indexOf(currentHash) === 0
    );

    // Scroll page to section of matching hash
    if (currentHash && hashMatchIndex > -1) {
      setActiveSection(hashMatchIndex);
      scrollPage(hashMatchIndex);
    }
  }, []);

  useEffect(() => {
    // Set the active index based on scroll position
    refs.forEach((ref, index) => {
      const margin = 200;
      // True for all sections except last in list
      if (index < refs.length - 1 && ref.current && refs[index + 1].current) {
        // Determine if scroll position is within the current div
        if (
          scrollPosition >= ref.current.offsetTop - margin &&
          scrollPosition < refs[index + 1].current!.offsetTop - margin
        ) {
          setActiveSection(index);
        }
      }
      // Checks if scroll position is within the last section
      else if (ref.current && scrollPosition > ref.current.offsetTop - margin) {
        setActiveSection(index);
      }
    });

    const handleScroll = () => {
      setScrollPosition(window.pageYOffset);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrollPosition]);

  // As page scrolls or section changes, the nav bar needs to be scrolled to
  useEffect(() => {
    // Wait for nav to close on mobile
    if (!isNavOpen) {
      setTimeout(() => {
        scrollPageNav();
      }, 10);
    }
  }, [activeSection, sectionToTopDistance, isNavOpen]);

  const getScrollMarginTop = () => {
    if (window) {
      const htmlElement = window.document.querySelector("html");
      if (htmlElement) {
        const scrollMarginTop = window
          .getComputedStyle(htmlElement, ":target")
          .getPropertyValue("scroll-margin-top");
        const scrollMarginValue = parseInt(scrollMarginTop);
        // Outputs Number here
        if (scrollMarginValue) return scrollMarginValue;
      }
    }
    return 100; // default offset
  };

  const scrollPage = (index: number) => {
    window.scrollTo({
      top: (refs[index].current?.offsetTop || 0) - getScrollMarginTop(),
      behavior: "smooth",
    });
    setIsNavOpen(false);
  };

  return (
    <CapabilityNavigationBase
      // Determine if navigation is stuck to top
      scrolledToTop={
        (refs[0] &&
          refs[0].current &&
          scrollPosition > refs[0].current.offsetTop - 125) ||
        false
      }
    >
      <JumpText>Jump to:</JumpText>
      <CapabilityNavigationContent>
        <ScrollContainer ref={nav} isOpened={isNavOpen}>
          <PageNav>
            {props.pageNavigation.map(
              (section, index) =>
                section &&
                section.primary?.title1 && (
                  <li
                    key={index}
                    className={index == activeSection ? "active" : ""} // Give ref activeSectionRef only if it's the active section
                    ref={activeSection == index ? activeSectionRef : null}
                  >
                    <Section
                      href={`#${
                        replaceWithDashes(section.primary?.title1) || ""
                      }`}
                      active={index == activeSection}
                      onClick={(e) => {
                        scrollPage(index);
                      }}
                    >
                      {section.primary?.title1}
                    </Section>
                  </li>
                )
            )}
          </PageNav>
        </ScrollContainer>
        <ArrowContainer
          isOpened={isNavOpen}
          onClick={() => {
            setIsNavOpen(!isNavOpen);
          }}
        >
          <ScrollArrow css={FA.AngleDownSolid} isOpened={isNavOpen} />
        </ArrowContainer>
      </CapabilityNavigationContent>
    </CapabilityNavigationBase>
  );
};

export default PageNavigation;
